import type { StoreDayOpeningTimeFragment } from '@shared/gql/document-nodes'
import type { FC } from 'react'

import { StoreOpeningTimeRangeLabel } from '@shared/gql/document-nodes'
import {
  Alert,
  AlertType,
  OpeningHoursOpenDuotone24,
  OpeningHoursOutline24,
} from '@sok/design-system'
import { toDateTime } from 'domain/date-time'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'

import { StoreInfoCardItem } from '../StoreInfoCard'
import { OpeningTime } from './OpeningTime'
import { useOpeningTime } from './StoreOpeningTimes'

// dates from api response are in Europe/Helsinki timezone
// date comparisons will be done in utc
const buildDateTimeObj = (date: string, time: string) => toDateTime(`${date}T${time}`)

const useIsStoreOpen = <T extends Pick<StoreDayOpeningTimeFragment, 'ranges' | 'mode' | 'date'>>(
  openingTime?: T,
): boolean => {
  const isOpen = useMemo(() => {
    if (!openingTime) {
      return false
    }

    switch (openingTime.mode) {
      case StoreOpeningTimeRangeLabel.Range: {
        const currentDateTime = DateTime.utc()

        return (
          openingTime.ranges?.some((range) => {
            const openDateTime = buildDateTimeObj(openingTime.date, range.open)
            const closeDateTime = buildDateTimeObj(openingTime.date, range.close).plus({
              days: range.closeOnSameDay ? 0 : 1,
            })

            return currentDateTime >= openDateTime && currentDateTime < closeDateTime
          }) ?? false
        )
      }
      case StoreOpeningTimeRangeLabel.AllDay:
        return true
      case StoreOpeningTimeRangeLabel.Closed:
      default:
        return false
    }
  }, [openingTime])

  return isOpen
}

const BlankIcon = () => null

export const TodaysOpeningTime: FC<{
  openingTime?: Pick<
    StoreDayOpeningTimeFragment,
    'mode' | 'exceptional' | 'message' | 'date' | 'ranges'
  >
}> = ({ openingTime }) => {
  const { t } = useTranslation()
  const getOpeningTime = useOpeningTime()
  const isStoreOpen = useIsStoreOpen(openingTime)
  const theme = useTheme()

  if (!openingTime) {
    return null
  }

  const getStoreOpeningTimeStatus = () => {
    switch (openingTime.mode) {
      case StoreOpeningTimeRangeLabel.Closed:
        return t('Closed')
      case StoreOpeningTimeRangeLabel.AllDay:
      case StoreOpeningTimeRangeLabel.Range:
        return `${t('Open today')}: ${getOpeningTime(openingTime)}`
      default:
        return null
    }
  }

  return (
    <>
      <StoreInfoCardItem aria-label={isStoreOpen ? t('Open now') : t('Closed now')}>
        {isStoreOpen ? (
          <OpeningHoursOpenDuotone24 secondaryColor={theme.colors.primary500} />
        ) : (
          <OpeningHoursOutline24 color={theme.colors.grey400} />
        )}
        <OpeningTime isExceptional={openingTime.exceptional ?? false}>
          {getStoreOpeningTimeStatus()}
        </OpeningTime>
      </StoreInfoCardItem>

      {(openingTime.exceptional || openingTime.message?.fi) && (
        <Alert
          message={openingTime.message?.fi ?? t('Exceptional hours for the day')}
          type={AlertType.INFO}
          customizeIcon={<BlankIcon />}
          data-test-id="exceptional-hours-message"
        />
      )}
    </>
  )
}
